import { useCallback, useRef } from 'react';

export type UseExtractCodeFromScanOptions = {
	customQrCodePrefixes?: string[];
};

// Cheqroom QR labels encode a URL (http(s)://cheqroom.com|dev/qr/<code>, or a workspace's
// custom prefix) rather than the bare code.
function extractCodeFromScan(code: string, customQrCodePrefixes?: string[]): string {
	if (code.match(/(.*)cheqroom\.(com|dev)\/qr\//)) {
		return code.replace(/(.*)cheqroom\.(com|dev)\/qr\//, '');
	}

	const prefix = customQrCodePrefixes?.find((prefix) => code.includes(prefix));
	if (prefix) return code.replace(prefix, '');

	return code;
}

/**
 * Returns a stable callback that strips a scanned QR/barcode value down to its bare code,
 * always reading the latest `customQrCodePrefixes` without changing identity — safe to use
 * inside a listener registered once (e.g. a native scan SDK's `didScan` callback).
 */
export function useExtractCodeFromScan({
	customQrCodePrefixes,
}: UseExtractCodeFromScanOptions): (code: string) => string {
	const customQrCodePrefixesRef = useRef(customQrCodePrefixes);
	customQrCodePrefixesRef.current = customQrCodePrefixes;

	return useCallback((code: string) => extractCodeFromScan(code, customQrCodePrefixesRef.current), []);
}
